/**
 * URL ↔ form values (de)serialization for 患者情報検索.
 * Enables bookmarkable search results per VisitPatientPage pattern.
 */

import type { SearchPatientListFormValues } from '@/schemas/searchPatientListSchema';
import type {
  BinaryFlag,
  BirthDurationMode,
  SearchDurationMode,
  SearchPatientListParams,
  SortRadioMode,
} from './types';

const intOrNull = (v: string | null): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const intOrDefault = <T extends number>(v: string | null, allowed: readonly T[], fallback: T): T => {
  const n = intOrNull(v);
  if (n != null && (allowed as readonly number[]).includes(n)) return n as T;
  return fallback;
};

const binaryOrNull = (v: string | null): BinaryFlag | null => {
  const n = intOrNull(v);
  return n === 0 || n === 1 ? (n as BinaryFlag) : null;
};

/**
 * Read initial page number from URL (defaults to 1).
 */
export function searchParamsToPage(sp: URLSearchParams): number {
  const n = intOrNull(sp.get('page'));
  return n && n >= 1 ? n : 1;
}

export function searchParamsToFormValues(sp: URLSearchParams): SearchPatientListFormValues | null {
  if ([...sp.keys()].length === 0) return null;

  return {
    visit_from_yearmonth: sp.get('visit_from_yearmonth') ?? '',
    visit_to_yearmonth: sp.get('visit_to_yearmonth') ?? '',
    search_duration: intOrDefault(sp.get('search_duration'), [0, 1, 2] as const, 0) as SearchDurationMode,
    insurer_type_id: sp.getAll('insurer_type_id').map(Number).filter(Number.isFinite),
    from_age: intOrNull(sp.get('from_age')),
    to_age: intOrNull(sp.get('to_age')),
    birth_month: intOrNull(sp.get('birth_month')) ?? 0,
    birth_duration: intOrDefault(sp.get('birth_duration'), [0, 1, 2] as const, 0) as BirthDurationMode,
    sex_id: intOrNull(sp.get('sex_id')),
    has_spouse: binaryOrNull(sp.get('has_spouse')),
    has_children: binaryOrNull(sp.get('has_children')),
    address: sp.get('address') ?? '',
    free_words: sp.get('free_words') ?? '',
    dm_reason_type_id: sp.getAll('dm_reason_type_id').map(Number).filter(Number.isFinite),
    dm: sp.get('dm') === '1',
    include: sp.get('include') === '1',
    sort_radio: intOrDefault(sp.get('sort_radio'), [0, 1, 2, 3] as const, 0) as SortRadioMode,
  };
}

/**
 * Serialize form values (+ optional current page) into URLSearchParams.
 * Pagination keys stay out of AppTable's "hasFilters" check (see DEFAULT_NON_FILTER_PARAMS),
 * so including `page` here is safe for the empty-state logic.
 */
export function formValuesToSearchParams(
  values: SearchPatientListFormValues,
  page?: number
): URLSearchParams {
  const out = new URLSearchParams();
  if (values.search_duration) {
    if (values.visit_from_yearmonth) out.set('visit_from_yearmonth', values.visit_from_yearmonth);
    if (values.visit_to_yearmonth) out.set('visit_to_yearmonth', values.visit_to_yearmonth);
  }
  if (values.search_duration) out.set('search_duration', String(values.search_duration));
  values.insurer_type_id?.forEach(id => out.append('insurer_type_id', String(id)));
  if (values.from_age != null) out.set('from_age', String(values.from_age));
  if (values.to_age != null) out.set('to_age', String(values.to_age));
  if (values.birth_month) out.set('birth_month', String(values.birth_month));
  if (values.birth_duration) out.set('birth_duration', String(values.birth_duration));
  if (values.sex_id != null) out.set('sex_id', String(values.sex_id));
  if (values.has_spouse != null) out.set('has_spouse', String(values.has_spouse));
  if (values.has_children != null) out.set('has_children', String(values.has_children));
  if (values.address) out.set('address', values.address);
  if (values.free_words) out.set('free_words', values.free_words);
  values.dm_reason_type_id?.forEach(id => out.append('dm_reason_type_id', String(id)));
  if (values.dm) out.set('dm', '1');
  if (values.include) out.set('include', '1');
  if (values.sort_radio) out.set('sort_radio', String(values.sort_radio));
  if (page && page > 1) out.set('page', String(page));
  return out;
}

export function formValuesToSearchPayload(values: SearchPatientListFormValues): SearchPatientListParams {
  const hasSearchDuration = values.search_duration !== 0;
  return {
    visit_from_yearmonth: hasSearchDuration ? values.visit_from_yearmonth || undefined : undefined,
    visit_to_yearmonth: hasSearchDuration ? values.visit_to_yearmonth || undefined : undefined,
    search_duration: values.search_duration,
    insurer_type_id: values.insurer_type_id?.length ? values.insurer_type_id : undefined,
    from_age: values.from_age,
    to_age: values.to_age,
    birth_month: values.birth_month || undefined,
    birth_duration: values.birth_duration,
    sex_id: values.sex_id,
    has_spouse: values.has_spouse,
    has_children: values.has_children,
    address: values.address || undefined,
    free_words: values.free_words || undefined,
    dm_reason_type_id: values.dm_reason_type_id?.length ? values.dm_reason_type_id : undefined,
    dm: values.dm || undefined,
    include: values.include || undefined,
    sort_radio: values.sort_radio,
  };
}
